import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

import './FAQs.scss';
import * as Util from '../../Util';

const faqs = [
  {
    q: 'How much does Wabi cost?',
    plain:
      'Your first 3 opt-out requests are free. After that, Wabi is $1.99 a month or $19.99 a year (about $1.67/mo — 16% off). Paid plans also include a one-click bulk opt-out that stops prescreened credit card offers from every major bureau in a single request. Cancel anytime, 30-day money-back guarantee.',
    a: (
      <>
        <p>
          Your first <strong>3 opt-out requests are free</strong> — try Wabi and
          see it work before paying a cent.
        </p>
        <p>
          After that, Wabi is <strong>$1.99 a month</strong>, or{' '}
          <strong>$19.99 a year</strong> (about $1.67/mo — 16% off). Less than
          a coffee a month.
        </p>
        <p>
          Paid plans also unlock a <strong>one-click bulk opt-out for
          prescreened credit card offers</strong> — the single biggest source
          of household junk mail, stopped across every major bureau in one
          request.
        </p>
        <p>
          Cancel anytime, and every paid plan is backed by a{' '}
          <strong>30-day money-back guarantee</strong>.
        </p>
      </>
    ),
  },
  {
    q: 'Will Wabi affect mail I actually want?',
    plain:
      'No. Wabi only opts you out of the senders you add. Your bills, government mail, tax forms, and packages are never touched.',
    a: (
      <>
        <p>
          No. Wabi only opts you out of the junk senders you add to your
          dashboard. Your bills, checks, tax forms, government mail, and
          packages are never touched.
        </p>
      </>
    ),
  },
  {
    q: 'How effective is Wabi?',
    plain:
      'Wabi handles most catalogs, credit-card offers, magazines, and yellow pages very well. The one thing no one can remove is USPS "Every Door Direct Mail" — unaddressed mail carpet-bombed across zip codes. That needs legislation.',
    a: (
      <>
        <p>
          Wabi handles most catalogs, credit-card offers, magazines, and yellow
          pages very well — any mailer that uses a customer list.
        </p>
        <p>
          The one thing no one can remove is USPS <em>Every Door Direct Mail</em>
          {' '}(EDDM) — unaddressed mail carpet-bombed across zip codes. Getting
          rid of that requires legislation, not an opt-out.
        </p>
      </>
    ),
  },
  {
    q: 'How long until my junk mail stops?',
    plain:
      'First opt-outs are sent within a week of signup. Most customers see a noticeable drop by week 4 and roughly 86% less junk by week 8. Some mailers pre-print 1–3 campaigns at once, so a few pieces can arrive after the opt-out clears.',
    a: (
      <>
        <p>
          First opt-outs are sent within a week of signup. Most customers see
          a noticeable drop by week 4 and roughly <strong>86% less junk by
          week 8</strong>.
        </p>
        <p>
          Some mailers pre-print 1–3 campaigns at once to save on printing, so
          a few pieces can still arrive after your opt-out has cleared. This
          is the same delay you'd see calling the sender yourself.
        </p>
      </>
    ),
  },
  {
    q: 'What if it doesn\u2019t work for me?',
    plain:
      'Wabi comes with a 30-day money-back guarantee. If you\u2019re not happy for any reason in the first 30 days, email us at hello@meetwabi.com and we\u2019ll refund your subscription in full. No hoops.',
    a: (
      <>
        <p>
          Wabi comes with a <strong>30-day money-back guarantee</strong>. If
          you're not happy for any reason in the first 30 days, email us at{' '}
          <a href="mailto:hello@meetwabi.com">hello@meetwabi.com</a> and we'll
          refund your subscription in full. No hoops.
        </p>
      </>
    ),
  },
  {
    q: 'How do you protect my address and data?',
    plain:
      'Your address is used only to send opt-out requests to the senders you add. We never sell, rent, or share it for marketing. Data is encrypted in transit and at rest.',
    a: (
      <>
        <p>
          Your address is used only to identify you to the senders you opt out
          of. We never sell, rent, or share it for marketing, and it's
          encrypted in transit and at rest.
        </p>
        <p>
          Full details are in our{' '}
          <a href="/privacy">privacy policy</a>.
        </p>
      </>
    ),
  },
  {
    q: 'Can I cancel anytime?',
    plain:
      'Yes. Cancel in one click from your dashboard — no calls, no forms. Opt-outs we\u2019ve already sent stay in effect.',
    a: (
      <>
        <p>
          Yes. Cancel in one click from your dashboard — no calls, no forms.
          Any opt-outs we've already sent on your behalf stay in effect.
        </p>
      </>
    ),
  },
  {
    q: 'Do I need a separate account for each person in my household?',
    plain:
      'Yes. Junk mail is sent to each person by name, so each household member needs their own Wabi plan to opt themselves out. Annual pricing helps when adding multiple members.',
    a: (
      <>
        <p>
          Yes. Junk mailers address each person by name, so each household
          member needs their own Wabi plan to opt themselves out. The annual
          plan helps when adding multiple members.
        </p>
      </>
    ),
  },
];

const FAQItem = ({ q, a, isOpen, onToggle }) => (
  <div className={`FAQs__item${isOpen ? ' is-open' : ''}`}>
    <button
      type="button"
      className="FAQs__question"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span className="FAQs__questionText">{q}</span>
      <span className="FAQs__chevron" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="14" height="14">
          <path
            d="M3 6l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
    {isOpen && <div className="FAQs__answer">{a}</div>}
  </div>
);

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (i) => {
    const nextOpen = openIndex === i ? null : i;
    setOpenIndex(nextOpen);
    if (nextOpen !== null && Util.isProduction()) {
      if (window.amplitude) {
        window.amplitude
          .getInstance()
          .logEvent('FAQ Opened', { question: faqs[i].q });
      }
      if (window.mixpanel) {
        window.mixpanel.track('FAQ Opened', { question: faqs[i].q });
      }
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.plain,
      },
    })),
  };

  return (
    <section id="faqs" className="FAQs">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <div className="FAQs__header">
        <h2>Questions, answered.</h2>
        <p>
          Everything people ask us before signing up. If something's missing,{' '}
          <a href="mailto:hello@meetwabi.com">email us</a>.
        </p>
      </div>

      <div className="FAQs__grid">
        {faqs.map((f, i) => (
          <FAQItem
            key={f.q}
            q={f.q}
            a={f.a}
            isOpen={openIndex === i}
            onToggle={() => handleToggle(i)}
          />
        ))}
      </div>
    </section>
  );
};

export default FAQs;
